import { CusTomTimelineRow } from './mock.ts';

const VIDEO_URL_PATTERN = /\.(mp4|webm|mov|m4v|avi)(\?|#|$)/i;

const isVideoLikeUrl = (value: unknown): value is string =>
  typeof value === 'string' &&
  (VIDEO_URL_PATTERN.test(value) || /^blob:|^data:/i.test(value));

export const extractTimelineActions = (data: CusTomTimelineRow[]) => {
  // Initialize an array to store all extracted actions
  const allActions: any[] = [];

  // Loop through each item in the data array
  data.forEach((item) => {
    // If the item has actions, process them
    if (item.actions && Array.isArray(item.actions)) {
      // Extract relevant properties from each action
      const processedActions = item.actions.map((action) => ({
        effectId: action.effectId,
        start: action.start,
        end: action.end,
        id: action.id,
        // If you need specific properties from action.data, extract only those
        data: {
          src: action.data?.src,
          name: action.data?.name,
          imageData: action.data?.imageData,
          sceneId: action.data?.sceneId,
          videoData: action.data?.videoData,
          provider: action.data?.provider,
          // Exclude the ref object which might contain circular references
        },
      }));

      // Add item identifier information with the actions
      allActions.push({
        itemId: item.id,
        itemName: item.name,
        actions: processedActions,
      });
    }
  });

  return allActions;
};

export const isGeneratedVideoAction = (
  action: {
    id?: string;
    data?: {
      videoData?: unknown;
      provider?: string;
    };
  }
): boolean => {
  if (!action.id?.startsWith('background-action-')) return false;

  return (
    (action.data?.provider === 'seedance' ||
      action.data?.provider === 'upload') &&
    isVideoLikeUrl(action.data.videoData)
  );
};

export const decodeName = (audioUrl: string) => {
  const parts = audioUrl.split('/');
  const fileName = parts[parts.length - 1];
  return decodeURIComponent(fileName.split('?')[0].split('.')[0]);
};

export const parseTimeToSeconds = (time: string): number => {
  const parts = time.split(':').map(Number);

  const [minutes, seconds, milliseconds] = [
    parts[0] || 0,
    parts[1] || 0,
    parts[2] || 0,
  ];

  return minutes * 60 + seconds + milliseconds / 1000;
};

export interface StitchSegment {
  id: string;
  audioURL: string;
  sourceUrl: string;
  startTime: number;
  endTime: number;
  sourceType: 'video' | 'audio';
}

/**
 * Flattens a timeline into the ordered list of video/audio segments that can be
 * sent to the backend stitching endpoints. Video-backed scenes (generated or
 * uploaded) are exposed as full video segments while standalone audio clips are
 * exposed as audio segments, so a single payload can drive both
 * /audio/stitchAudio (which extracts/mixes the audio) and /video/stitchVideo
 * (which muxes it in).
 */
export const buildStitchSegments = (
  data: CusTomTimelineRow[]
): StitchSegment[] =>
  data.reduce((segments: StitchSegment[], item) => {
    const itemSegments = item.actions.flatMap((action): StitchSegment[] => {
      if (action.id.startsWith('audio-action-')) {
        return [
          {
            id: action.id,
            audioURL: action.data.src,
            sourceUrl: action.data.src,
            startTime: action.start,
            endTime: action.end,
            sourceType: 'audio' as const,
          },
        ];
      }

      if (isGeneratedVideoAction(action)) {
        const sourceUrl = action.data?.videoData || '';

        if (!sourceUrl.trim()) {
          return [];
        }

        return [
          {
            id: `${action.id}-video`,
            audioURL: sourceUrl.trim(),
            sourceUrl: sourceUrl.trim(),
            startTime: action.start,
            endTime: action.end,
            sourceType: 'video' as const,
          },
        ];
      }

      return [];
    });

    segments.push(...itemSegments);
    return segments;
  }, []);

/**
 * Returns true when the timeline only contains scenes that are backed by
 * stitched video sources plus standalone audio (i.e. no text, characters,
 * effects, rigged characters or props that would need to be drawn on the
 * canvas). In that case the final video can be produced entirely by the
 * backend stitching endpoint, skipping the canvas recording step during
 * publish.
 */
export const hasOnlyVideoScenesAndAudio = (
  data: CusTomTimelineRow[]
): boolean => {
  if (!data.length) return false;

  let hasGeneratedVideoScene = false;

  const onlyStitchableActions = data.every((segment) =>
    segment.actions.every((action) => {
      if (isGeneratedVideoAction(action)) {
        hasGeneratedVideoScene = true;
        return true;
      }

      return action.id?.startsWith('audio-action');
    })
  );

  return onlyStitchableActions && hasGeneratedVideoScene;
};
